import React, { useState, useEffect } from 'react';
import { Trash } from '@saleshandy/icons';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../../../shared/design-system/components/atoms/modal';
import { DropdownCreateUpdateOption, DropdownTypeOption } from './types';
import Select from '../../../../../../shared/design-system/components/select';
import Button from '../../../../../../shared/design-system/components/atoms/button';

interface DeleteOptionModalProps {
  show: boolean;
  options: string[];
  optionToDelete?: string;
  onClose: () => void;
  onSubmit: (selectedOption: string) => void;
  isRequestPending: boolean;
}

const UNASSIGNED_KEY = 'unassigned';

const DeleteOptionModal: React.FC<DeleteOptionModalProps> = ({
  show,
  options,
  optionToDelete,
  onClose,
  onSubmit,
  isRequestPending,
}) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  // Filter out the option being deleted from the list
  const filteredOptions = options.filter((opt) => opt !== optionToDelete);

  // Add "Unassigned" as a static option at the beginning
  const allOptions = [
    { name: 'Unassigned', key: UNASSIGNED_KEY },
    ...filteredOptions.map((opt) => ({ name: opt, key: opt })),
  ];

  const handleSubmit = () => {
    if (selectedOption !== null) {
      // If "Unassigned" is selected, pass empty string, otherwise pass the selected option
      const migrateToOption =
        selectedOption === UNASSIGNED_KEY ? '' : selectedOption;
      onSubmit(migrateToOption);
    }
  };

  return (
    <Modal
      show={show}
      className="delete-modal"
      titleContent={
        <div className="d-flex align-items-center">
          <Trash width={23} height={23} className="red-txt-12" />
          <div className="delete-title">
            Do you want to delete this Dropdown Option?
          </div>
        </div>
      }
      showCloseIcon={false}
      onClose={onClose}
      cancelButtonText={t('labels.cancel')}
      submitButtonText={t('labels.delete')}
      isSubmitLoading={isRequestPending}
      isSubmitDisabled={!selectedOption || isRequestPending}
      onSubmit={handleSubmit}
      cancelButtonVarient={Button.Variant.Outlined}
      onHide={onClose}
    >
      <div className="d-flex flex-column gap-16">
        <span className="regular-2 popover-arrow-color-txt">
          Deleting this dropdown option will leave all associated prospects
          uncategorized.
        </span>
        <div className="d-flex flex-column gap-24">
          <span className="regular-2 popover-arrow-color-txt">
            Please select a new dropdown option for them.
          </span>
          <Select<DropdownTypeOption>
            options={allOptions}
            selectedOptionKey={selectedOption}
            onChange={([option]) => setSelectedOption(option.key)}
            placeholder="Select the dropdown option."
            optionRenderer={(option) => <span>{option.name}</span>}
            selectedOptionRenderer={([option]) => <span>{option.name}</span>}
          />
        </div>
      </div>
    </Modal>
  );
};

export default DeleteOptionModal;
